import { LicenseService } from "app/core/services/license.service";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccountService } from "app/core/services/account.service";
import { AdminService } from "app/core/services/admin.service";
import { WhiteLabelService } from "app/core/white-label/white-label.service";
import { RouterEventService } from "app/core/services/router-event.service";
import { Trunkset } from "app/shared/models/trunkset.interface";
import { TrunksetDataService } from "app/features/nconnect-voice/services/api/trunkset-data/trunkset-data.service";
import { NconnectAccountSelectionService } from "app/features/nconnect-voice/services/nconnect-account-selection/nconnect-account-selection.service";
import { AdminUser } from "app/shared/models/admin-user";
import { NconnectItemProviderService } from "app/features/nconnect-voice/services/nconnect-item-provider/nconnect-item-provider.service";
import { MatDialog } from "@angular/material/dialog";
import { InfoComponent } from "app/shared/components/info/info.component";
import { SidenavItemComponent } from "app/shared/components/sidenav-item/sidenav-item.component";
import { TranslateModule } from "@ngx-translate/core";
import { LogoComponent } from "app/shared/components/logo/logo.component";
import { CommonModule } from "@angular/common";
import { HoverboxComponent } from "app/shared/components/hoverbox/hoverbox.component";

@Component({
  selector: "admin-sidenav",
  templateUrl: "./sidenav.component.html",
  styleUrls: ["./sidenav.component.scss"],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    SidenavItemComponent,
    TranslateModule,
    LogoComponent,
    CommonModule,
    HoverboxComponent,
  ],
})
export class SidenavComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  @Output() public itemClicked: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  public activeUser: AdminUser | null;

  public nconnectItemName$: Observable<string>;
  public routeParamId: number;
  public pbxEndpointMenuItems$: Observable<Trunkset[] | null> =
    this.trunksetDataService.trunksets$;
  public firstTrunksetId$: Observable<number> =
    this.trunksetDataService.firstTrunksetId$;

  // Nconnect Sidenav Item Visibility
  public isBlacklistItemShown: boolean;
  public isBlacklistEntryItemShown: boolean;
  public isPbxEndpointItemShown: boolean;
  public isNumberRoutingItemsShown: boolean;
  public isTemplateItemShown: boolean;
  public isServiceItemShown: boolean;
  public isNumberblockEntryItemShown: boolean;
  public nconnectVoiceProductName: { nconnectVoiceProductName: string } = {
    nconnectVoiceProductName:
      this.whiteLabelService.data.nconnectVoiceProductName,
  };

  // User Sidenav Item Visibility
  public isKAccountSelected: boolean;
  public isUserPageActive: boolean;

  // Wizard Sidenav Item Visibility
  public isWizardPageActive: boolean;

  public isNconnectPageActive: boolean;

  public hasConferenceServices$: Observable<boolean> =
    this.licenseService.isUserHasConferenceServiceLicense$;
  public hasFaxServices$: Observable<boolean> =
    this.licenseService.isUserHasFaxServiceLicense$;

  constructor(
    protected adminService: AdminService,
    private accountService: AccountService,
    private whiteLabelService: WhiteLabelService,
    private routerEventService: RouterEventService,
    private trunksetDataService: TrunksetDataService,
    private nconnectItemProviderService: NconnectItemProviderService,
    private nconnectAccountSelectionService: NconnectAccountSelectionService,
    private licenseService: LicenseService,
    private dialog: MatDialog,
  ) {
    adminService.emitChange
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((adminUser: AdminUser) => {
        this.activeUser = adminUser || null;
      });
  }

  public ngOnInit(): void {
    // Listen for route changes
    this.routerEventService.routeChanged$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((currentUrl: string) => {
        this.setSidenavItemVisibility(currentUrl);
      });

    // Listen for Route param id changes
    this.routerEventService.routeParamIdChanged$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((routeParamId: number) => {
        this.routeParamId = routeParamId;
      });

    // Listen for account changes
    this.accountService.selectedAccount$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.setMainSidenavItemVisibility();
      });

    this.nconnectItemName$ = this.nconnectItemProviderService.nconnectItemName$;
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Emit a value to parent component when clicking on sidenav menu item
   */
  public sideNavItemClicked(hasSubItems: boolean): void {
    this.itemClicked.emit(hasSubItems);
  }

  private setMainSidenavItemVisibility(): void {
    this.isKAccountSelected = this.accountService.isKAccountSelected;
  }

  private setSidenavItemVisibility(currentUrl: string): void {
    if (this.checkIfItemCanBeShown(["nconnect-voice"], currentUrl)) {
      this.setNconnectItemVisibility(currentUrl);
    }

    this.isUserPageActive = this.checkIfItemCanBeShown(["user"], currentUrl);
    this.isWizardPageActive = this.checkIfItemCanBeShown(
      ["wizard"],
      currentUrl,
    );

    this.isNconnectPageActive = this.checkIfItemCanBeShown(
      ["nconnect", "/"],
      currentUrl,
    );
  }

  /**
   * Check which Nconnect Sidenav Items can be shown
   */
  private setNconnectItemVisibility(currentUrl: string): void {
    this.isNumberRoutingItemsShown = this.checkIfItemCanBeShown(
      ["number-routing", "numberblock", "backupnumbers"],
      currentUrl,
    );
    this.isBlacklistItemShown = this.checkIfItemCanBeShown(
      ["trunkset", "blacklist"],
      currentUrl,
    );

    this.isBlacklistEntryItemShown = this.checkIfItemCanBeShown(
      ["blacklist"],
      currentUrl,
      true,
    );
    this.isNumberblockEntryItemShown = this.checkIfItemCanBeShown(
      ["numberblock"],
      currentUrl,
      true,
    );

    this.isPbxEndpointItemShown = this.checkIfItemCanBeShown(
      ["pbx-endpoint/"],
      currentUrl,
    );
    this.isTemplateItemShown = this.checkIfItemCanBeShown(
      ["pbx-endpoint-template"],
      currentUrl,
    );
    this.isServiceItemShown = this.checkIfItemCanBeShown(
      ["services"],
      currentUrl,
    );
  }

  private checkIfItemCanBeShown(
    allowedUrls: string[],
    activeUrl: string,
    isSubEntryItem?: boolean,
  ): boolean {
    // Check if item can be visible for sub entry items like Blacklist Entry or NumberBlock Entry
    if (isSubEntryItem) {
      return (
        activeUrl.includes(allowedUrls[0]) &&
        !activeUrl.endsWith(allowedUrls[0])
      );
    }

    return !!allowedUrls.find((allowedUrl: string) =>
      activeUrl.includes(allowedUrl),
    );
  }

  public showAppInfo(): void {
    this.dialog.open(InfoComponent);
  }
}
